import { useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useToggleReactionMutation } from 'generated/graphql';
import { applyReactionToggle } from '../lib/applyReactionToggle';
import { CommunityId } from '../model/community.types';
import { ReactionParent, ReactionType } from '../model/post.types';

type CachedReaction = {
  __typename: 'Reaction';
  type: ReactionType;
  count: number;
  isToggled: boolean;
};

type ToggleReactionInput = {
  parent: ReactionParent;
  entityId: string;
  type: ReactionType;
};

export const useToggleReaction = (communityId: CommunityId) => {
  const [toggleReactionMutation] = useToggleReactionMutation();

  return useCallback(
    ({ parent, entityId, type }: ToggleReactionInput) =>
      toggleReactionMutation({
        variables: { communityId, entityId, parent, type },
        optimisticResponse: {
          __typename: 'Mutation',
          toggleReaction: { __typename: 'Post', id: entityId },
        },
        update: (cache) => {
          if (parent !== ReactionParent.Post) return;
          const cacheId = cache.identify({ __typename: 'Post', id: entityId });
          if (cacheId === undefined) return;

          cache.modify<{ reactions: CachedReaction[] }>({
            id: cacheId,
            fields: {
              // Reaction has no id, so the cache stores reactions inline and never as
              // References.
              reactions: (existing = []) =>
                applyReactionToggle(
                  [...(existing as readonly CachedReaction[])],
                  type,
                ).map((reaction) => ({
                  __typename: 'Reaction' as const,
                  ...reaction,
                })),
            },
          });
        },
      }).catch(() => {
        toast.error('Something went wrong');
      }),
    [toggleReactionMutation, communityId],
  );
};
